// Topbar — sticky, glass, search, bell, user menu with logout
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, LogOut, ChevronDown, UserCog, CheckCheck, Inbox, CloudOff, LifeBuoy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { ROLE_COLOURS, ROLE_LABELS } from "@/lib/constants";
import { TID } from "@/lib/testIds";
import GlobalSearch from "@/components/GlobalSearch";
import { usePendingUploadsCount, PendingUploadsPanel } from "@/components/PendingUploadsPanel";
import { openSupport, reopenHelpButton } from "@/components/NeedHelpButton";

function timeAgo(iso) {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

export default function Topbar({ title, subtitle, branded = false, logoUrl = null, bannerUrl = null, brandBg = null }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [bellOpen, setBellOpen] = useState(false);
    const [uploadsOpen, setUploadsOpen] = useState(false);
    const { items, unreadTotal, markRead, markAllRead } = useNotifications();
    const pendingUploadsCount = usePendingUploadsCount();

    const openNotification = (n) => {
        setBellOpen(false);
        if (!n.read) markRead(n.id);
        if (n.link) navigate(n.link);
    };

    return (
        <div
            className={`sticky top-0 z-20 flex items-center gap-4 px-7 py-3.5 border-b ${branded ? "" : "backdrop-blur-md"}`}
            style={branded ? {
                borderColor: "transparent",
                backgroundColor: brandBg || "#C75A1B",
                ...(bannerUrl ? {
                    // Banner sits UNDER a semi-transparent brand overlay so it stays
                    // vivid/visible (multiply against a dark brand crushed it to black).
                    // Overlay is stronger at the edges where the title/bell/user sit,
                    // much lighter in the middle so the artwork clearly reads through.
                    backgroundImage: `linear-gradient(90deg, ${brandBg || "#C75A1B"}f2 0%, ${brandBg || "#C75A1B"}cc 18%, ${brandBg || "#C75A1B"}40 50%, ${brandBg || "#C75A1B"}cc 82%, ${brandBg || "#C75A1B"}f2 100%), url("${bannerUrl}")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    minHeight: "88px",
                } : {}),
            } : {
                borderColor: "var(--theme-border)",
                background: "rgba(245,243,240,.85)",
            }}
        >
            {branded && logoUrl && (
                <img src={logoUrl} alt={title} data-testid="topbar-wide-logo"
                     className="h-9 max-w-[180px] object-contain object-left drop-shadow" />
            )}
            <div data-testid={TID.topbarTitle}>
                <h1 className={`text-[20px] font-bold leading-tight ${branded ? "text-white" : ""}`}>{title}</h1>
                {subtitle ? (
                    <p className="text-[12.5px] mt-0.5" style={{ color: branded ? "rgba(255,255,255,.8)" : "#6F6A64" }}>
                        {subtitle}
                    </p>
                ) : null}
            </div>
            <div className="flex-1" />

            <GlobalSearch />

            <button
                data-testid="topbar-help"
                aria-label="Help & Support"
                title="Help & Support"
                onClick={() => { reopenHelpButton(); openSupport(); }}
                className="w-9 h-9 rounded-[9px] grid place-items-center bg-white border transition-colors hover:border-[var(--brand-accent,_#C75A1B)] hover:text-[var(--brand-accent,_#C75A1B)]"
                style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}
            >
                <LifeBuoy className="w-4 h-4" />
            </button>

            {pendingUploadsCount > 0 && (
                <div className="relative">
                    <button
                        data-testid="topbar-pending-uploads"
                        aria-label="Pending photo uploads"
                        onClick={() => setUploadsOpen((v) => !v)}
                        className="relative w-9 h-9 rounded-[9px] grid place-items-center bg-white border transition-colors hover:border-[#9F4212]"
                        style={{ borderColor: "#E5E0DA", color: "#9F4212" }}
                    >
                        <CloudOff className="w-4 h-4" />
                        <span data-testid="topbar-pending-uploads-count"
                            className="absolute -top-1.5 -right-1.5 min-w-[17px] h-[17px] px-1 rounded-full bg-[#9F4212] text-white text-[10px] font-bold grid place-items-center border-[1.5px] border-white">
                            {pendingUploadsCount > 9 ? "9+" : pendingUploadsCount}
                        </span>
                    </button>
                    {uploadsOpen && <PendingUploadsPanel onClose={() => setUploadsOpen(false)} />}
                </div>
            )}

            <div className="relative">
                <button
                    data-testid={TID.topbarBell}
                    aria-label="Notifications"
                    onClick={() => setBellOpen((v) => !v)}
                    className="relative w-9 h-9 rounded-[9px] grid place-items-center bg-white border transition-colors hover:border-[var(--brand-accent,_#C75A1B)] hover:text-[var(--brand-accent,_#C75A1B)]"
                    style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}
                >
                    <Bell className="w-4 h-4" />
                    {unreadTotal > 0 && (
                        <span data-testid="topbar-bell-count"
                            className="absolute -top-1.5 -right-1.5 min-w-[17px] h-[17px] px-1 rounded-full bg-[#B33A2E] text-white text-[10px] font-bold grid place-items-center border-[1.5px] border-white">
                            {unreadTotal > 9 ? "9+" : unreadTotal}
                        </span>
                    )}
                </button>
                {bellOpen && (
                    <>
                        <div className="fixed inset-0 z-30" onClick={() => setBellOpen(false)} />
                        <div data-testid="notifications-dropdown"
                            className="absolute right-0 top-[120%] bg-white border rounded-xl shadow-brand min-w-[340px] max-w-[380px] z-40 animate-fade-in overflow-hidden"
                            style={{ borderColor: "#E5E0DA" }}>
                            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                                <span className="text-[13px] font-bold text-[#1F1F1F]">Notifications</span>
                                {unreadTotal > 0 && (
                                    <button data-testid="notifications-mark-all" onClick={() => markAllRead()}
                                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--brand-accent,_#C75A1B)] hover:underline">
                                        <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                                    </button>
                                )}
                            </div>
                            <div className="max-h-[380px] overflow-y-auto">
                                {items.length === 0 ? (
                                    <div className="px-4 py-8 text-center text-[12px] text-[#9A968F]">
                                        <Inbox className="w-5 h-5 mx-auto mb-1.5 opacity-60" /> You're all caught up.
                                    </div>
                                ) : items.map((n) => (
                                    <button key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => openNotification(n)}
                                        className="w-full text-left px-4 py-2.5 border-b flex gap-2.5 hover:bg-[#F6F4F0] transition-colors"
                                        style={{ borderColor: "#F5F2EE", background: n.read ? "#fff" : "#FBF6F1" }}>
                                        {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: "var(--brand-accent,#C75A1B)" }} />}
                                        <div className={`flex-1 min-w-0 ${n.read ? "pl-[18px]" : ""}`}>
                                            <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{n.title}</div>
                                            {n.body && <div className="text-[11px] text-[#6F6A64] truncate">{n.body}</div>}
                                            <div className="text-[10px] text-[#9A968F] mt-0.5">{timeAgo(n.created_at)}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {user && (
                <div className="relative">
                    <button
                        data-testid={TID.userMenu}
                        onClick={() => setMenuOpen((v) => !v)}
                        className={`flex items-center gap-2 pl-1 pr-2 py-1 rounded-[9px] border border-transparent transition-colors ${branded ? "hover:bg-white/15" : "hover:bg-white hover:border-[#E5E0DA]"}`}
                    >
                        <div
                            className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold"
                            style={{
                                background: `${ROLE_COLOURS[user.role]}1A`,
                                color: ROLE_COLOURS[user.role],
                                border: `1px solid ${ROLE_COLOURS[user.role]}66`,
                            }}
                        >
                            {user.avatar_initials}
                        </div>
                        <span className={`hidden lg:block text-[12px] font-semibold ${branded ? "text-white" : "text-[#1F1F1F]"}`}>{user.name}</span>
                        <ChevronDown className="w-3.5 h-3.5" style={{ color: branded ? "rgba(255,255,255,.85)" : "#6F6A64" }} />
                    </button>
                    {menuOpen && (
                        <div
                            className="absolute right-0 top-[110%] bg-white border rounded-lg shadow-brand min-w-[220px] py-1 z-30 animate-fade-in"
                            style={{ borderColor: "#E5E0DA" }}
                        >
                            <div className="px-3 py-2 border-b" style={{ borderColor: "#F0EDE8" }}>
                                <div className="text-[12px] font-semibold text-[#1F1F1F]">{user.name}</div>
                                <div className="text-[10.5px]" style={{ color: "#6F6A64" }}>{user.email}</div>
                                <div className="text-[10.5px] mt-1 italic" style={{ color: ROLE_COLOURS[user.role] }}>
                                    {ROLE_LABELS[user.role]}
                                </div>
                            </div>
                            <button
                                data-testid="topbar-account"
                                onClick={() => { setMenuOpen(false); navigate("/app/account"); }}
                                className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] text-[#1F1F1F] hover:bg-[#F6F4F0]"
                            >
                                <UserCog className="w-3.5 h-3.5" /> My account
                            </button>
                            <button
                                data-testid={TID.logout}
                                onClick={async () => { setMenuOpen(false); await logout(); window.location.href = "/login"; }}
                                className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] text-[#1F1F1F] hover:bg-[#FBEFE5] hover:text-[#9F4212]"
                            >
                                <LogOut className="w-3.5 h-3.5" /> Sign out
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
